/*
 * Behavior: the first user interaction happens too soon after the page loads
 * (less than 50ms) — faster than a person can see the page and react.
 *
 * Time is measured from page load via event.timeStamp / performance.now(), both
 * counted in milliseconds from when the page started loading.
 *
 * A React effect only attaches its listeners after hydration, which is usually
 * later than 50ms, so on its own it would miss the earliest interactions. The
 * head script in pages/_document.tsx records the first interaction before
 * hydration on window.__saBotFirstInteraction; this hook reads that on mount and
 * otherwise listens for interactions that happen afterwards.
 */
import { useEffect, useRef } from 'react'
import { TrackBotTrafficAction } from 'lib/bots/trackBotTraffic'

// Faster than a person can see the page and react, in milliseconds => synthetic.
const MIN_HUMAN_REACTION_MS = 50
// Anything in this list counts as a user interaction. Keep in sync with the
// head script in pages/_document.tsx.
// Only deliberate, discrete actions are counted. 'mousemove' is excluded because
// leftover cursor motion carrying into an instant (cached) page load could fire
// within 50ms and is not a real reaction; 'scroll'/'wheel' are excluded because
// the browser fires early programmatic scrolls (URL-bar collapse, scroll restore).
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'click']

const useTimingBehaviorBotCheck = (report: (action: TrackBotTrafficAction) => void): void => {
  const hasInteracted = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    // First interaction time (ms since page load) captured by the head script
    // before this hook mounted, if there was one.
    const firstInteractionTimeMs = window.__saBotFirstInteraction
    if (typeof firstInteractionTimeMs === 'number') {
      hasInteracted.current = true
      if (firstInteractionTimeMs < MIN_HUMAN_REACTION_MS) {
        report('timing')
      }
      // The first interaction is already handled, so there is nothing left to
      // listen for.
      return
    }

    const handleInteraction = (event: Event) => {
      if (hasInteracted.current) {
        return
      }
      hasInteracted.current = true

      INTERACTION_EVENTS.forEach((eventType) => {
        window.removeEventListener(eventType, handleInteraction, { capture: true })
      })

      const interactionTimeMs =
        typeof performance !== 'undefined' ? performance.now() : event.timeStamp

      if (interactionTimeMs < MIN_HUMAN_REACTION_MS) {
        report('timing')
      }
    }

    INTERACTION_EVENTS.forEach((eventType) => {
      window.addEventListener(eventType, handleInteraction, { passive: true, capture: true })
    })

    return () => {
      INTERACTION_EVENTS.forEach((eventType) => {
        window.removeEventListener(eventType, handleInteraction, { capture: true })
      })
    }
  }, [])
}

export default useTimingBehaviorBotCheck
